import { Flex, Text } from '@chakra-ui/react';
import {
  Banner,
  Button,
  ButtonText,
  FormFeedback,
  Radio,
  useToast
} from '@morressier/alembic';
import { IDecisionModalProps } from '../decisionModalHelper';
import { Overlay } from 'src/components/Overlay';
import { Layout } from 'src/components/Layout';
import { Controller, useForm } from 'react-hook-form';
import { TransferTargetJournalInfo } from '../../MySubmission/components/TargetJournalInfo';
import { useAcceptTransfer } from 'src/api/commands';
import { fetchMissingFieldsFromArticleId } from 'src/api/queries.js';
import { formatDeadlineDate } from 'src/utils/date';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { useNavigate } from 'react-router-dom';

type IFormData = {
  id: string;
  name: string;
  articleWorkflowId: string;
  articleTypeCategoryId: string;
};

export const AcceptTransferModal = ({
  onClose,
  submissionId,
  submissionContextId
}: IDecisionModalProps) => {
  const navigate = useNavigate();
  const toast = useToast();
  const {
    workflowResponse: {
      articleWorkflow: {
        articleTypeCategoryId: oldArticleTypeCategoryId,
        id: oldArticleWorkflowId
      }
    },
    transferOffer,
    organizationId
  } = useSubmissionData();

  const acceptTransferMutation = useAcceptTransfer(
    submissionId,
    organizationId
  );

  const {
    handleSubmit,
    formState: { errors },
    control,
    setValue
  } = useForm({
    defaultValues: {
      id: '',
      name: '',
      articleWorkflowId: '',
      articleTypeCategoryId: ''
    }
  });

  const error = errors.id;

  const onSubmit = async ({
    id,
    name,
    articleWorkflowId,
    articleTypeCategoryId
  }: IFormData) => {
    fetchMissingFieldsFromArticleId({
      submissionId,
      articleWorkflowId,
      roleContext: 'author'
    })
      .then(async ({ hasMissingFields }) => {
        const res = await acceptTransferMutation.mutateAsync({
          submissionContextId: id,
          articleWorkflowId,
          subtype: hasMissingFields ? 'fields' : 'form',
          oldSubmissionContextId: submissionContextId,
          oldArticleWorkflowId,
          articleTypeCategoryId,
          oldArticleTypeCategoryId
        });

        if (!res.isSuccess)
          return toast({
            variant: 'error',
            title: 'Error accepting transfer offer',
            description:
              res.message?.errors
                ?.map((err: { reason: string }) => err.reason)
                .join(', ') || 'Try again later!'
          });

        const deadline = res.submission.workflowData.currentDeadline;
        if (hasMissingFields) {
          toast({
            variant: 'success',
            title: 'Incomplete Transfer',
            description: (
              <Flex gap={2} alignItems={'flex-start'}>
                <Text>
                  Your submission was transferred. Please review and verify the submission details
                  {deadline ? ` by ${formatDeadlineDate(deadline)} ` : ' '}
                  to proceed with editor assignment.
                </Text>
                <ButtonText
                  width={'150px'}
                  onClick={() =>
                    navigate(`/app/my-submissions/${submissionId}`)
                  }
                >
                  View
                </ButtonText>
              </Flex>
            ),
            isClosable: false
          });
        } else {
          toast({
            variant: 'success',
            title: 'Transfer Concluded',
            description: `Your Submission will be assigned to an Editor in ${name}.`,
            isClosable: true
          });
        }

        navigate('/app/my-submissions');
      })
      .catch(error =>
        toast({
          variant: 'error',
          title: 'Error Validating Transfer Compatibility',
          description: error.response.data?.message ?? error.message
        })
      );
  };

  return (
    <Overlay>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex flexDirection="column" backgroundColor="surfaceSecondaryFill">
          <Layout width="52rem">
            <Flex gap={8} flexDirection="column">
              <Flex flexDirection="column">
                <Text textStyle="headingSection">
                  Select Journal to Transfer
                </Text>
                <Text>
                  If you accept this offer, you can choose one Journal to
                  proceed with the publication process.
                </Text>
              </Flex>

              <Banner
                content="Some Journals may require additional data fields not included in your original submission. After transferring, you may need to provide missing information to proceed."
                heading="For your attention"
                variant="attention"
              />

              <Flex flexDirection="column" gap={2}>
                {!!transferOffer?.targetJournals.length && (
                  <Controller
                    name="id"
                    control={control}
                    rules={{ required: 'This field is required.' }}
                    render={({ field: { onChange, value } }) => (
                      <Flex flexDirection="column" gap={4}>
                        {transferOffer.targetJournals.map(journal => {
                          const { articleWorkflowId, articleTypeCategoryId } =
                            journal;

                          return (
                            articleWorkflowId && (
                              <Radio
                                key={journal.id}
                                name="id"
                                value={journal.id}
                                onChange={() => {
                                  onChange(journal.id);
                                  setValue(
                                    'articleWorkflowId',
                                    articleWorkflowId
                                  );
                                  setValue('name', journal.name);
                                  setValue(
                                    'articleTypeCategoryId',
                                    articleTypeCategoryId
                                  );
                                }}
                                checked={value === journal.id}
                                ariaLabel={journal.name}
                                id={journal.id}
                              >
                                <TransferTargetJournalInfo journal={journal} />
                              </Radio>
                            )
                          );
                        })}
                      </Flex>
                    )}
                  />
                )}
                {error && (
                  <FormFeedback variant="error">
                    Select a Journal to proceed.
                  </FormFeedback>
                )}
              </Flex>
            </Flex>
          </Layout>
          <Flex
            gap={2}
            justifyContent="flex-end"
            width={'100%'}
            backgroundColor="surfacePrimaryFill"
            position={'sticky'}
            bottom={0}
            paddingX={16}
            paddingY={4}
          >
            <Button
              isDisabled={acceptTransferMutation.isLoading}
              onClick={onClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button type="submit" isLoading={acceptTransferMutation.isLoading}>
              Accept Transfer
            </Button>
          </Flex>
        </Flex>
      </form>
    </Overlay>
  );
};
